import type { ReactNode } from 'react';
import { VirtualList } from '../ui/VirtualList.js';

/**
 * R16 (decision #8) — the completion scoreboard. "You're clear" at zero, plus the
 * server-backed Done-today tally (survives refresh, unlike a client counter).
 */
export function QueueScoreboard({
  openCount,
  doneToday,
}: {
  openCount: number;
  doneToday: number;
}) {
  return (
    <span data-testid="queue-scoreboard">
      {openCount === 0 ? '✅ You’re clear' : `${openCount} to review`}
      {doneToday > 0 ? ` · ${doneToday} done today` : ''}
    </span>
  );
}

/**
 * R16 — the ONE queue frame the five queue surfaces share (inbox + the four
 * outreach tenants R17–R20). Three panes — LIST · WORK CANVAS · CONTEXT RAIL —
 * with a slot contract validated against all four modals:
 *
 *  - header: title · count · search · filter chips · bulk-overflow (⋯)
 *  - canvas header: Queue | History tabs · a capability badge
 *  - canvas: the selected item's work surface (draft/composer/detail)
 *  - context rail: author / profile / facts
 *  - scoreboard: the "you're clear" / Done-today completion line
 *
 * Tenants own their data + mutations and supply the slots; the frame owns layout,
 * selection plumbing, the virtualized list, and the empty/loading/error states.
 */
export type QueueTab = 'queue' | 'history' | 'welcomes';

export interface QueuePageProps<T> {
  title: string;
  /** Open-item count shown next to the title. */
  count?: number;

  // list
  items: T[];
  getId: (item: T) => string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  renderRow: (item: T, selected: boolean) => ReactNode;
  /** ~row height for the virtualizer estimate. */
  rowEstimate?: number;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  emptyTitle: string;
  emptyHint: string;
  /** Infinite scroll (optional). */
  onReachEnd?: () => void;

  // header slots
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  filterChips?: ReactNode;
  /** Content of the bulk-overflow (⋯) menu; omit to hide the menu. */
  bulkMenu?: ReactNode;

  // canvas
  tab: QueueTab;
  onTabChange: (tab: QueueTab) => void;
  /** A short capability/posture badge (e.g. "shadow · review only"). */
  capabilityBadge?: ReactNode;
  renderCanvas: (selected: T | null) => ReactNode;
  /** Rendered when tab === 'history' (the durable log). */
  renderHistory?: () => ReactNode;
  /**
   * batch-411 - an optional third tab, today only the connection requests' held welcomes. Rendered in
   * place of the canvas when tab === 'welcomes'; `count` (the number waiting) shows on the tab.
   */
  welcomesTab?: { count: number; render: () => ReactNode };

  // rail + scoreboard
  renderContext?: (selected: T | null) => ReactNode;
  /** The "you're clear — N done today" completion line. */
  scoreboard?: ReactNode;

  testId?: string;
}

export function QueuePage<T>({
  title,
  count,
  items,
  getId,
  selectedId,
  onSelect,
  renderRow,
  rowEstimate = 84,
  isLoading,
  isError,
  onRetry,
  emptyTitle,
  emptyHint,
  onReachEnd,
  search,
  filterChips,
  bulkMenu,
  tab,
  onTabChange,
  capabilityBadge,
  renderCanvas,
  renderHistory,
  welcomesTab,
  renderContext,
  scoreboard,
  testId,
}: QueuePageProps<T>) {
  const selected = selectedId ? (items.find((it) => getId(it) === selectedId) ?? null) : null;
  const selectedIndex = selectedId ? items.findIndex((it) => getId(it) === selectedId) : -1;

  return (
    <div className="queue-page" data-testid={testId}>
      <section className="queue-list" aria-label={`${title} list`}>
        <header className="queue-list-head">
          <div className="queue-list-title">
            <h2>{title}</h2>
            {typeof count === 'number' ? <span className="queue-count">{count}</span> : null}
            <div className="queue-list-spacer" />
            {bulkMenu ? (
              <details className="unified-overflow">
                <summary
                  className="unified-overflow-btn"
                  data-testid="queue-bulk"
                  aria-label={`${title} bulk actions`}
                  title="Bulk actions"
                >
                  ⋯
                </summary>
                <div className="unified-overflow-menu">{bulkMenu}</div>
              </details>
            ) : null}
          </div>
          {search ? (
            <input
              type="search"
              className="queue-search"
              data-testid="queue-search"
              placeholder={search.placeholder ?? `Search ${title.toLowerCase()}`}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
            />
          ) : null}
          {filterChips ? <div className="queue-chips">{filterChips}</div> : null}
        </header>

        {scoreboard ? <div className="queue-scoreboard">{scoreboard}</div> : null}

        {isLoading ? (
          <div className="queue-state" data-testid="queue-loading">
            Loading…
          </div>
        ) : isError ? (
          <div className="queue-state" data-testid="queue-error">
            <p>Couldn’t load {title.toLowerCase()}.</p>
            {onRetry ? (
              <button type="button" onClick={onRetry}>
                Retry
              </button>
            ) : null}
          </div>
        ) : items.length === 0 ? (
          <div className="queue-state queue-empty" data-testid="queue-empty">
            <strong>{emptyTitle}</strong>
            <span>{emptyHint}</span>
          </div>
        ) : (
          <VirtualList
            className="queue-vlist"
            testId="queue-vlist"
            ariaLabel={title}
            items={items}
            getKey={getId}
            estimateSize={rowEstimate}
            onReachEnd={onReachEnd}
            scrollToIndex={selectedIndex >= 0 ? selectedIndex : null}
            renderItem={(item) => {
              const isSel = getId(item) === selectedId;
              const id = getId(item);
              // R17 review — a focusable div (role=button) rather than a <button> so a
              // tenant row may legally contain its own interactive controls (R19's
              // reaction palette / comment send) without nesting buttons.
              return (
                // biome-ignore lint/a11y/useSemanticElements: a focusable div (not a <button>) so tenant rows may contain their own interactive controls
                <div
                  role="button"
                  tabIndex={0}
                  className={isSel ? 'queue-row selected' : 'queue-row'}
                  aria-current={isSel ? 'true' : undefined}
                  data-testid="queue-row"
                  onClick={() => onSelect(id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(id);
                    }
                  }}
                >
                  {renderRow(item, isSel)}
                </div>
              );
            }}
          />
        )}
      </section>

      <section className="queue-canvas" aria-label={`${title} work`}>
        <header className="queue-canvas-head">
          <div className="queue-tabs" role="tablist" aria-label="Queue or history">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'queue'}
              data-testid="queue-tab-queue"
              className={tab === 'queue' ? 'active' : ''}
              onClick={() => onTabChange('queue')}
            >
              Queue
            </button>
            {renderHistory ? (
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'history'}
                data-testid="queue-tab-history"
                className={tab === 'history' ? 'active' : ''}
                onClick={() => onTabChange('history')}
              >
                History
              </button>
            ) : null}
            {welcomesTab ? (
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'welcomes'}
                data-testid="queue-tab-welcomes"
                className={tab === 'welcomes' ? 'active' : ''}
                onClick={() => onTabChange('welcomes')}
              >
                Welcomes{welcomesTab.count > 0 ? ` (${welcomesTab.count})` : ''}
              </button>
            ) : null}
          </div>
          {capabilityBadge ? <div className="queue-capability">{capabilityBadge}</div> : null}
        </header>
        <div className="queue-canvas-body">
          {tab === 'history' && renderHistory
            ? renderHistory()
            : tab === 'welcomes' && welcomesTab
              ? welcomesTab.render()
              : renderCanvas(selected)}
        </div>
      </section>

      {renderContext ? (
        <aside className="queue-context" aria-label={`${title} context`}>
          {renderContext(selected)}
        </aside>
      ) : null}
    </div>
  );
}
